import emailjs from "@emailjs/browser";

// Initialize EmailJS with your public key
const EMAILJS_PUBLIC_KEY = process.env.REACT_APP_EMAILJS_PUBLIC_KEY;
const EMAILJS_SERVICE_ID = process.env.REACT_APP_EMAILJS_SERVICE_ID;
const EMAILJS_CONTACT_TEMPLATE_ID =
    process.env.REACT_APP_EMAILJS_CONTACT_TEMPLATE_ID;
const EMAILJS_DEMO_TEMPLATE_ID = process.env.REACT_APP_EMAILJS_DEMO_TEMPLATE_ID;
const COMPANY_EMAIL = process.env.REACT_APP_COMPANY_EMAIL;

if (EMAILJS_PUBLIC_KEY) {
    emailjs.init(EMAILJS_PUBLIC_KEY);
}

// Email template for company notification - Contact form
export const formatCompanyEmail = ({
    name,
    email,
    phone,
    subject,
    message,
}) => {
    return `
New Contact Form Submission
-------------------------

Name:    ${name}
Email:   ${email}
Phone:   ${phone || "N/A"}
Subject: ${subject}

Message:
${message}
`;
};

// Email template for sender confirmation - Contact form
export const formatSenderEmail = ({ name, subject }) => {
    return `
Hi ${name},

Thank you for reaching out to us regarding "${subject}".
We have received your message and will get back to you as soon as possible.

Best regards,
Simplitechs
`;
};

// Email template for Book a Demo company notification
export const formatBookDemoCompanyEmail = ({
    name,
    company,
    phone,
    datetime,
    email,
    message,
}) => {
    return `
New Book a Demo Request
----------------------

Name:    ${name}
Company: ${company}
Phone:   ${phone}
Email:   ${email}
Preferred Date/Time: ${datetime}

Message:
${message || "N/A"}
`;
};

// Email template for Book a Demo sender confirmation
export const formatBookDemoSenderEmail = ({
    name,
    company,
    phone,
    datetime,
    email,
    message,
}) => {
    return `
Hi ${name},

Thank you for your interest in booking a demo with us!
We have received your request with the following details:

Name:    ${name}
Company: ${company}
Phone:   ${phone}
Email:   ${email}
Preferred Date/Time: ${datetime}

${message ? `Your message: ${message}\n` : ""}
Our team will contact you soon to confirm your demo.

Best regards,
Simplitechs
`;
};

/**
 * Send contact form email
 * @param {Object} data - Contact form data
 * @param {string} data.name - User's name
 * @param {string} data.email - User's email
 * @param {string} data.phone - User's phone number
 * @param {string} data.subject - Email subject
 * @param {string} data.message - Email message
 * @returns {Promise} EmailJS response
 */
export const sendContactEmail = async (data) => {
    const { name, email, phone, subject, message } = data;

    // Validate required fields
    if (!name || !email || !subject || !message) {
        throw new Error("Missing required fields.");
    }

    try {
        // Check if EmailJS is initialized
        if (!EMAILJS_PUBLIC_KEY || !EMAILJS_SERVICE_ID) {
            throw new Error(
                "EmailJS configuration is missing. Please set environment variables."
            );
        }

        // Send to company
        await emailjs.send(EMAILJS_SERVICE_ID, EMAILJS_CONTACT_TEMPLATE_ID, {
            to_email: COMPANY_EMAIL,
            from_email: email,
            user_name: name,
            user_email: email,
            user_phone: phone || "N/A",
            subject: `[Contact] ${subject}`,
            message: message,
            reply_to: email,
        });

        // Send confirmation to user
        await emailjs.send(EMAILJS_SERVICE_ID, EMAILJS_CONTACT_TEMPLATE_ID, {
            to_email: email,
            from_email: COMPANY_EMAIL,
            user_name: name,
            subject: `We received your message: ${subject}`,
            message: formatSenderEmail({ name, subject }),
        });

        return { success: true };
    } catch (error) {
        console.error("Error sending contact email:", error);
        throw new Error(error.message || "Failed to send email.");
    }
};

/**
 * Send book demo request email
 * @param {Object} data - Book demo form data
 * @param {string} data.name - User's name
 * @param {string} data.email - User's email
 * @param {string} data.company - Company name
 * @param {string} data.phone - Phone number
 * @param {string} data.datetime - Preferred date/time
 * @param {string} data.message - Optional message
 * @returns {Promise} EmailJS response
 */
export const sendBookDemoEmail = async (data) => {
    const { name, email, company, phone, datetime, message } = data;

    // Validate required fields
    if (!email || !name || !company || !phone || !datetime) {
        throw new Error("Missing required fields.");
    }

    try {
        // Check if EmailJS is initialized
        if (!EMAILJS_PUBLIC_KEY || !EMAILJS_SERVICE_ID) {
            throw new Error(
                "EmailJS configuration is missing. Please set environment variables."
            );
        }

        // Send to company
        await emailjs.send(EMAILJS_SERVICE_ID, EMAILJS_DEMO_TEMPLATE_ID, {
            to_email: COMPANY_EMAIL,
            from_email: email,
            user_name: name,
            user_company: company,
            user_phone: phone,
            user_email: email,
            preferred_datetime: datetime,
            message: message || "N/A",
            reply_to: email,
        });

        // Send confirmation to user
        await emailjs.send(EMAILJS_SERVICE_ID, EMAILJS_DEMO_TEMPLATE_ID, {
            to_email: email,
            from_email: COMPANY_EMAIL,
            user_name: name,
            user_company: company,
            user_phone: phone,
            user_email: email,
            preferred_datetime: datetime,
            message: formatBookDemoSenderEmail({
                name,
                company,
                phone,
                datetime,
                email,
                message,
            }),
        });

        return { success: true };
    } catch (error) {
        console.error("Error sending book demo email:", error);
        throw new Error(error.message || "Failed to send demo booking email.");
    }
};
